/**
 * Gestion de la connexion par reconnaissance faciale, depuis le profil.
 *
 * L'enregistrement est la pièce qui manquait : sans lui, la connexion faciale
 * n'était utilisable par personne. La bibliothèque et ses modèles (~7 Mo) ne
 * sont téléchargés qu'au moment où le joueur clique sur « Enregistrer mon
 * visage » — jamais à l'ouverture du profil.
 */

import React, { useState, useEffect, Suspense, lazy } from 'react';
import { FaCamera, FaTrash, FaCheckCircle, FaInfoCircle } from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from '../../api/axios';

const BiometricLogin = lazy(() => import('../BiometricLogin'));

function BiometricSection() {
  const [biometries, setBiometries] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [enregistrement, setEnregistrement] = useState(false);
  const [suppression, setSuppression]       = useState(null);

  const charger = () => {
    api.get('/biometric/list')
      .then(({ data }) => setBiometries(data.biometrics || data.biometries || []))
      .catch(() => setBiometries([]))
      .finally(() => setLoading(false));
  };

  useEffect(charger, []);

  const supprimer = async (bio) => {
    if (!window.confirm('Supprimer cet enregistrement ? Tu ne pourras plus te connecter avec ce visage.')) return;
    setSuppression(bio.id);
    try {
      await api.delete(`/biometric/${bio.id}`);
      toast.success('Enregistrement supprimé');
      setBiometries((prev) => prev.filter((b) => b.id !== bio.id));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setSuppression(null);
    }
  };

  const visages = biometries.filter((b) => b.biometric_type === 'facial_recognition');

  return (
    <div className="rounded-3xl p-5 border border-white/10 mb-4"
      style={{ background: 'rgba(0,0,0,0.3)' }}>
      <div className="flex items-center gap-2 mb-1">
        <FaCamera className="text-qfl-gold" size={14} />
        <h3 className="text-qfl-gold font-black text-sm uppercase tracking-wider"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          Connexion par reconnaissance faciale
        </h3>
      </div>
      <p className="text-white/40 text-xs mb-4 leading-relaxed">
        Connecte-toi d'un regard, sans saisir ton mot de passe.
      </p>

      {/* Honnêteté sur ce que ça vaut : un confort, pas un renforcement */}
      <div className="flex items-start gap-2 p-3 rounded-xl mb-4"
        style={{ background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.2)' }}>
        <FaInfoCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={11} />
        <p className="text-white/60 text-[11px] leading-relaxed">
          Ton visage n'est jamais envoyé en image : seules des mesures
          mathématiques le sont, et la caméra s'éteint dès que tu quittes l'écran.
          C'est un <strong className="text-white/80">raccourci de connexion</strong> —
          ton mot de passe reste la protection principale de ton compte.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <div className="w-6 h-6 border-2 border-qfl-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {visages.length > 0 && (
            <div className="space-y-2 mb-4">
              {visages.map((b) => (
                <div key={b.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.04]">
                  <FaCheckCircle className="text-green-400 flex-shrink-0" size={13} />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs font-semibold truncate">{b.device_name || 'Appareil web'}</p>
                    <p className="text-white/35 text-[10px]">
                      {b.last_used
                        ? `Dernière utilisation : ${new Date(b.last_used).toLocaleDateString('fr-FR')}`
                        : 'Jamais utilisé'}
                    </p>
                  </div>
                  <button
                    onClick={() => supprimer(b)}
                    disabled={suppression === b.id}
                    className="text-red-400/60 hover:text-red-400 p-2 disabled:opacity-40"
                  >
                    <FaTrash size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {!enregistrement ? (
            <button
              onClick={() => setEnregistrement(true)}
              className="w-full py-3 rounded-xl font-bold text-sm text-white/80 bg-white/5 border border-white/10 hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
            >
              <FaCamera size={13} />
              {visages.length > 0 ? 'Ajouter cet appareil' : 'Enregistrer mon visage'}
            </button>
          ) : (
            <div>
              <Suspense fallback={
                <div className="text-center py-6">
                  <div className="w-7 h-7 border-2 border-qfl-gold border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-white/50 text-xs">Chargement (environ 7 Mo, une seule fois)…</p>
                </div>
              }>
                <BiometricLogin
                  mode="register"
                  onLoginSuccess={() => {
                    setEnregistrement(false);
                    toast.success('Visage enregistré ! Tu peux maintenant te connecter d\'un regard.');
                    charger();
                  }}
                />
              </Suspense>
              <button
                onClick={() => setEnregistrement(false)}
                className="w-full mt-2 py-2 text-white/40 text-xs hover:text-white/70"
              >
                Annuler
              </button>
            </div>
          )}

          {visages.length === 0 && !enregistrement && (
            <p className="text-white/25 text-[11px] text-center mt-2">
              Environ 7 Mo seront téléchargés la première fois.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default BiometricSection;
